<script lang="ts">
	interface Address {
		street: string;
		suite?: string;
		city: string;
		state: string;
		zip: string;
		country?: string;
	}

	interface Props {
		entityName: string;
		domain: string;
		email: string;
		contactUrl: string;
		address: Address;
		lastUpdated: string;
	}

	let { entityName, domain, email, contactUrl, address, lastUpdated }: Props = $props();

	interface ListItem {
		title: string;
		content: string;
		collected?: string;
	}

	interface Additional {
		title: string;
		content: string;
	}

	interface Subsection {
		title: string;
		inShort?: string;
		content: string;
		list?: (string | ListItem)[];
		additional?: Additional[];
		footer?: string;
	}

	interface Section {
		title: string;
		inShort?: string;
		data: string;
		subsections?: Subsection[];
	}

	interface PersonalInfoCategory extends ListItem {
		collected: string;
	}

	function isPersonalInfoCategory(item: string | ListItem): item is PersonalInfoCategory {
		return typeof item === "object" && "collected" in item;
	}

	const siteUrl = `https://${domain}`;

	const sections: Section[] = [
		{
			title: `1. WHAT INFORMATION DO WE COLLECT?`,
			inShort: `We collect personal information that you provide to us.`,
			data: `We collect personal information that you voluntarily provide to us when you express an interest in obtaining information about us or our products and Services, when you participate in activities on the Services, or otherwise when you contact us.`,
			subsections: [
				{
					title: "Personal information you disclose to us",
					content: `The personal information that we collect depends on the context of your interactions with us and the Services, the choices you make, and the products and features you use. The personal information we collect may include the following:`,
					list: ["names", "phone numbers", "email addresses"],
					additional: [
						{
							title: "Sensitive Information",
							content: "We do not process sensitive information."
						}
					],
					footer: "All personal information that you provide to us must be true, complete, and accurate, and you must notify us of any changes to such personal information."
				},
				{
					title: "Information automatically collected",
					inShort:
						"Some information — such as your Internet Protocol (IP) address and/or browser and device characteristics — is collected automatically when you visit our Services.",
					content: `We automatically collect certain information when you visit, use, or navigate the Services. This information does not reveal your specific identity (like your name or contact information) but may include device and usage information, such as your IP address, browser and device characteristics, operating system, language preferences, referring URLs, device name, country, location, information about how and when you use our Services, and other technical information. This information is primarily needed to maintain the security and operation of our Services, and for our internal analytics and reporting purposes.`,
					list: [
						{
							title: "Log and Usage Data",
							content:
								'Log and usage data is service-related, diagnostic, usage, and performance information our servers automatically collect when you access or use our Services and which we record in log files. Depending on how you interact with us, this log data may include your IP address, device information, browser type, and settings and information about your activity in the Services (such as the date/time stamps associated with your usage, pages and files viewed, searches, and other actions you take such as which features you use), device event information (such as system activity, error reports (sometimes called "crash dumps"), and hardware settings).'
						},
						{
							title: "Device Data",
							content:
								"We collect device data such as information about your computer, phone, tablet, or other device you use to access the Services. Depending on the device used, this device data may include information such as your IP address (or proxy server), device and application identification numbers, location, browser type, hardware model, Internet service provider and/or mobile carrier, operating system, and system configuration information."
						},
						{
							title: "Location Data",
							content:
								"We collect location data such as information about your device's location, which can be either precise or imprecise. How much information we collect depends on the type and settings of the device you use to access the Services. For example, we may use GPS and other technologies to collect geolocation data that tells us your current location (based on your IP address). You can opt out of allowing us to collect this information either by refusing access to the information or by disabling your Location setting on your device. However, if you choose to opt out, you may not be able to use certain aspects of the Services."
						}
					]
				}
			]
		},
		{
			title: `2. HOW DO WE PROCESS YOUR INFORMATION?`,
			inShort: `We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent.`,
			data: `We process your personal information for a variety of reasons, depending on how you interact with our Services, including:`,
			subsections: [
				{
					title: "Processing Purposes",
					content: "",
					list: [
						{
							title: "To deliver and facilitate delivery of services to the user",
							content:
								"We may process your information to provide you with the requested service."
						},
						{
							title: "To respond to user inquiries/offer support to users",
							content:
								"We may process your information to respond to your inquiries and solve any potential issues you might have with the requested service."
						},
						{
							title: "To send you marketing and promotional communications",
							content:
								'We may process the personal information you send to us for our marketing purposes, if this is in accordance with your marketing preferences. You can opt out of our marketing emails at any time. For more information, see "WHAT ARE YOUR PRIVACY RIGHTS?" below.'
						},
						{
							title: "To evaluate and improve our Services, products, marketing, and your experience",
							content:
								"We may process your information when we believe it is necessary to identify usage trends, determine the effectiveness of our promotional campaigns, and to evaluate and improve our Services, products, marketing, and your experience."
						},
						{
							title: "To identify usage trends",
							content:
								"We may process information about how you use our Services to better understand how they are being used so we can improve them."
						},
						{
							title: "To determine the effectiveness of our marketing and promotional campaigns",
							content:
								"We may process your information to better understand how to provide marketing and promotional campaigns that are most relevant to you."
						},
						{
							title: "To comply with our legal obligations",
							content:
								"We may process your information to comply with our legal obligations, respond to legal requests, and exercise, establish, or defend our legal rights."
						}
					]
				}
			]
		},
		{
			title: `3. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?`,
			inShort: `We may share information in specific situations described in this section and/or with the following third parties.`,
			data: `We may need to share your personal information in the following situations:`,
			subsections: [
				{
					title: "Sharing Scenarios",
					content: "",
					list: [
						{
							title: "Business Transfers",
							content:
								"We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company."
						}
					]
				}
			]
		},
		{
			title: `4. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?`,
			inShort: `We may use cookies and other tracking technologies to collect and store your information.`,
			data: `We may use cookies and similar tracking technologies (like web beacons and pixels) to gather information when you interact with our Services. Some online tracking technologies help us maintain the security of our Services, prevent crashes, fix bugs, save your preferences, and assist with basic site functions.`,
			subsections: [
				{
					title: "Third-Party Tracking",
					content: `We also permit third parties and service providers to use online tracking technologies on our Services for analytics and advertising, including to help manage and display advertisements, to tailor advertisements to your interests, or to send abandoned shopping cart reminders (depending on your communication preferences). The third parties and service providers use their technology to provide advertising about products and services tailored to your interests which may appear either on our Services or on other websites.`,
					additional: [
						{
							title: "Opt-Out Information",
							content: `To the extent these online tracking technologies are deemed to be a "sale"/"sharing" (which includes targeted advertising, as defined under the applicable laws) under applicable US state laws, you can opt out of these online tracking technologies by submitting a request as described below under section "DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?"`
						},
						{
							title: "Cookie Notice",
							content:
								"Specific information about how we use such technologies and how you can refuse certain cookies is set out in our Cookie Notice."
						}
					]
				},
				{
					title: "Google Analytics",
					content: `We may share your information with Google Analytics to track and analyze the use of the Services. To opt out of being tracked by Google Analytics across the Services, visit https://tools.google.com/dlpage/gaoptout. For more information on the privacy practices of Google, please visit the Google Privacy & Terms page.`
				}
			]
		},
		{
			title: `5. HOW LONG DO WE KEEP YOUR INFORMATION?`,
			inShort: `We keep your information for as long as necessary to fulfill the purposes outlined in this Privacy Notice unless otherwise required by law.`,
			data: `We will only keep your personal information for as long as it is necessary for the purposes set out in this Privacy Notice, unless a longer retention period is required or permitted by law (such as tax, accounting, or other legal requirements). When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize such information, or, if this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.`
		},
		{
			title: `6. HOW DO WE KEEP YOUR INFORMATION SAFE?`,
			inShort: `We aim to protect your personal information through a system of organizational and technical security measures.`,
			data: `We have implemented appropriate and reasonable technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information. Although we will do our best to protect your personal information, transmission of personal information to and from our Services is at your own risk. You should only access the Services within a secure environment.`
		},
		{
			title: `7. DO WE COLLECT INFORMATION FROM MINORS?`,
			inShort: `We do not knowingly collect data from or market to children under 18 years of age.`,
			data: `We do not knowingly collect, solicit data from, or market to children under 18 years of age, nor do we knowingly sell such personal information. By using the Services, you represent that you are at least 18 or that you are the parent or guardian of such a minor and consent to such minor dependent's use of the Services. If we learn that personal information from users less than 18 years of age has been collected, we will deactivate the account and take reasonable measures to promptly delete such data from our records. If you become aware of any data we may have collected from children under age 18, please contact us at ${email}.`
		},
		{
			title: `8. WHAT ARE YOUR PRIVACY RIGHTS?`,
			inShort: `You may review, change, or terminate your account at any time, depending on your country, province, or state of residence.`,
			data: "",
			subsections: [
				{
					title: "Withdrawing your consent",
					content: `If we are relying on your consent to process your personal information, which may be express and/or implied consent depending on the applicable law, you have the right to withdraw your consent at any time. You can withdraw your consent at any time by contacting us by using the contact details provided in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" below.`,
					additional: [
						{
							title: "Important Note",
							content:
								"Please note that this will not affect the lawfulness of the processing before its withdrawal nor, when applicable law allows, will it affect the processing of your personal information conducted in reliance on lawful processing grounds other than consent."
						}
					]
				},
				{
					title: "Opting out of marketing and promotional communications",
					content: `You can unsubscribe from our marketing and promotional communications at any time by:`,
					list: [
						"Clicking on the unsubscribe link in the emails that we send",
						'Replying "STOP" or "UNSUBSCRIBE" to the SMS messages that we send',
						'Contacting us using the details provided in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?" below'
					],
					footer: "You will then be removed from the marketing lists. However, we may still communicate with you — for example, to send you service-related messages that are necessary for the administration and use of your account, to respond to service requests, or for other non-marketing purposes."
				},
				{
					title: "Contact Information",
					content: `If you have questions or comments about your privacy rights, you may email us at ${email}.`
				}
			]
		},
		{
			title: `9. CONTROLS FOR DO-NOT-TRACK FEATURES`,
			inShort: "",
			data: `Most web browsers and some mobile operating systems and mobile applications include a Do-Not-Track ("DNT") feature or setting you can activate to signal your privacy preference not to have data about your online browsing activities monitored and collected. At this stage, no uniform technology standard for recognizing and implementing DNT signals has been finalized. As such, we do not currently respond to DNT browser signals or any other mechanism that automatically communicates your choice not to be tracked online. If a standard for online tracking is adopted that we must follow in the future, we will inform you about that practice in a revised version of this Privacy Notice.`,
			subsections: [
				{
					title: "California DNT Notice",
					content: `California law requires us to let you know how we respond to web browser DNT signals. Because there currently is not an industry or legal standard for recognizing or honoring DNT signals, we do not respond to them at this time.`
				}
			]
		},
		{
			title: `10. DO UNITED STATES RESIDENTS HAVE SPECIFIC PRIVACY RIGHTS?`,
			inShort: `If you are a resident of California, Colorado, Connecticut, Delaware, Florida, Indiana, Iowa, Kentucky, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon, Tennessee, Texas, Utah, or Virginia, you may have the right to request access to and receive details about the personal information we maintain about you and how we have processed it, correct inaccuracies, get a copy of, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. These rights may be limited in some circumstances by applicable law. More information is provided below.`,
			data: "",
			subsections: [
				{
					title: "Categories of Personal Information We Collect",
					content: `We have collected the following categories of personal information in the past twelve (12) months:`,
					list: [
						{
							title: "A. Identifiers",
							content:
								"Contact details, such as real name, alias, postal address, telephone or mobile contact number, unique personal identifier, online identifier, Internet Protocol address, email address, and account name",
							collected: "YES"
						},
						{
							title: "B. Personal information as defined in the California Customer Records statute",
							content:
								"Name, contact information, education, employment, employment history, and financial information",
							collected: "YES"
						},
						{
							title: "C. Protected classification characteristics under state or federal law",
							content:
								"Gender, age, date of birth, race and ethnicity, national origin, marital status, and other demographic data",
							collected: "YES"
						},
						{
							title: "D. Commercial information",
							content:
								"Transaction information, purchase history, financial details, and payment information",
							collected: "YES"
						},
						{
							title: "E. Biometric information",
							content: "Fingerprints and voiceprints",
							collected: "NO"
						},
						{
							title: "F. Internet or other similar network activity",
							content:
								"Browsing history, search history, online behavior, interest data, and interactions with our and other websites, applications, systems, and advertisements",
							collected: "NO"
						},
						{
							title: "G. Geolocation data",
							content: "Device location",
							collected: "NO"
						},
						{
							title: "H. Audio, electronic, sensory, or similar information",
							content:
								"Images and audio, video or call recordings created in connection with our business activities",
							collected: "NO"
						},
						{
							title: "I. Professional or employment-related information",
							content:
								"Business contact details in order to provide you our Services at a business level or job title, work history, and professional qualifications if you apply for a job with us",
							collected: "NO"
						},
						{
							title: "J. Education Information",
							content: "Student records and directory information",
							collected: "NO"
						},
						{
							title: "K. Inferences drawn from collected personal information",
							content: `Inferences drawn from any of the collected personal information listed above to create a profile or summary about, for example, an individual's preferences and characteristics`,
							collected: "NO"
						},
						{
							title: "L. Sensitive personal Information",
							content: "",
							collected: "NO"
						}
					],
					additional: [
						{
							title: "Additional Collection Contexts",
							content: `We may also collect other personal information outside of these categories through instances where you interact with us in person, online, or by phone or mail in the context of:
- Receiving help through our customer support channels;
- Participation in customer surveys or contests; and
- Facilitation in the delivery of our Services and to respond to your inquiries.`
						},
						{
							title: "Retention Periods",
							content: `We will use and retain the collected personal information as needed to provide the Services or for:
- Category A - Indefinitely
- Category B - Indefinitely
- Category C - Indefinitely
- Category D - Indefinitely`
						}
					]
				},
				{
					title: "Sources of Personal Information",
					content:
						'Learn more about the sources of personal information we collect in "WHAT INFORMATION DO WE COLLECT?"'
				},
				{
					title: "How We Use and Share Personal Information",
					content:
						'Learn more about how we use your personal information in the section, "HOW DO WE PROCESS YOUR INFORMATION?"'
				},
				{
					title: "Will your information be shared with anyone else?",
					content: `We may disclose your personal information with our service providers pursuant to a written contract between us and each service provider. Learn more about how we disclose personal information to in the section, "WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?"

We may use your personal information for our own business purposes, such as for undertaking internal research for technological development and demonstration. This is not considered to be "selling" of your personal information.

We have not disclosed, sold, or shared any personal information to third parties for a business or commercial purpose in the preceding twelve (12) months. We will not sell or share personal information in the future belonging to website visitors, users, and other consumers.`
				},
				{
					title: "Your Rights",
					content: `You have rights under certain US state data protection laws. However, these rights are not absolute, and in certain cases, we may decline your request as permitted by law. These rights include:`,
					list: [
						"Right to know whether or not we are processing your personal data",
						"Right to access your personal data",
						"Right to correct inaccuracies in your personal data",
						"Right to request the deletion of your personal data",
						"Right to obtain a copy of the personal data you previously shared with us",
						"Right to non-discrimination for exercising your rights",
						`Right to opt out of the processing of your personal data if it is used for targeted advertising (or sharing as defined under California's privacy law), the sale of personal data, or profiling in furtherance of decisions that produce legal or similarly significant effects ("profiling")`
					],
					additional: [
						{
							title: "State-Specific Rights",
							content: `Depending upon the state where you live, you may also have the following rights:
- Right to access the categories of personal data being processed (as permitted by applicable law, including Minnesota's privacy law)
- Right to obtain a list of the categories of third parties to which we have disclosed personal data (as permitted by applicable law, including California's and Delaware's privacy law)
- Right to obtain a list of specific third parties to which we have disclosed personal data (as permitted by applicable law, including Minnesota's and Oregon's privacy law)
- Right to review, understand, question, and correct how personal data has been profiled (as permitted by applicable law, including Minnesota's privacy law)
- Right to limit use and disclosure of sensitive personal data (as permitted by applicable law, including California's privacy law)
- Right to opt out of the collection of sensitive data and personal data collected through the operation of a voice or facial recognition feature (as permitted by applicable law, including Florida's privacy law)`
						}
					]
				},
				{
					title: "How to Exercise Your Rights",
					content: `To exercise these rights, you can contact us by visiting ${siteUrl}${contactUrl}, by emailing us at ${email}, or by referring to the contact details at the bottom of this document.

We will honor your opt-out preferences if you enact the Global Privacy Control (GPC) opt-out signal on your browser.

Under certain US state data protection laws, you can designate an authorized agent to make a request on your behalf. We may deny a request from an authorized agent that does not submit proof that they have been validly authorized to act on your behalf in accordance with applicable laws.`
				},
				{
					title: "Request Verification",
					content: `Upon receiving your request, we will need to verify your identity to determine you are the same person about whom we have the information in our system. We will only use personal information provided in your request to verify your identity or authority to make the request. However, if we cannot verify your identity from the information already maintained by us, we may request that you provide additional information for the purposes of verifying your identity and for security or fraud-prevention purposes.

If you submit the request through an authorized agent, we may need to collect additional information to verify your identity before processing your request and the agent will need to provide a written and signed permission from you to submit such request on your behalf.`
				},
				{
					title: "Appeals",
					content: `Under certain US state data protection laws, if we decline to take action regarding your request, you may appeal our decision by emailing us at ${email}. We will inform you in writing of any action taken or not taken in response to the appeal, including a written explanation of the reasons for the decisions. If your appeal is denied, you may submit a complaint to your state attorney general.`
				},
				{
					title: 'California "Shine The Light" Law',
					content: `California Civil Code Section 1798.83, also known as the "Shine The Light" law, permits our users who are California residents to request and obtain from us, once a year and free of charge, information about categories of personal information (if any) we disclosed to third parties for direct marketing purposes and the names and addresses of all third parties with which we shared personal information in the immediately preceding calendar year. If you are a California resident and would like to make such a request, please submit your request in writing to us by using the contact details provided in the section "HOW CAN YOU CONTACT US ABOUT THIS NOTICE?"`
				}
			]
		},
		{
			title: `11. DO WE MAKE UPDATES TO THIS NOTICE?`,
			inShort: `Yes, we will update this notice as necessary to stay compliant with relevant laws.`,
			data: `We may update this Privacy Notice from time to time. The updated version will be indicated by an updated "Revised" date at the top of this Privacy Notice. If we make material changes to this Privacy Notice, we may notify you either by prominently posting a notice of such changes or by directly sending you a notification. We encourage you to review this Privacy Notice frequently to be informed of how we are protecting your information.`
		},
		{
			title: `12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?`,
			inShort: "",
			data: `If you have questions or comments about this notice, you may email us at ${email} or contact us by post at:`,
			subsections: [
				{
					title: "Mailing Address",
					content: [
						entityName,
						address.street,
						address.suite,
						`${address.city}, ${address.state} ${address.zip}`,
						address.country ?? "United States"
					]
						.filter(Boolean)
						.join("\n")
				}
			]
		},
		{
			title: `13. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?`,
			inShort: "",
			data: `Based on the applicable laws of your country or state of residence in the US, you may have the right to request access to the personal information we collect from you, details about how we have processed it, correct inaccuracies, or delete your personal information. You may also have the right to withdraw your consent to our processing of your personal information. These rights may be limited in some circumstances by applicable law.`,
			subsections: [
				{
					title: "Submit a Request",
					content: `To request to review, update, or delete your personal information, please visit: ${siteUrl}${contactUrl}.`
				}
			]
		}
	];

	interface KeyPoint {
		title: string;
		content: string;
		linkTo?: number;
	}

	const keyPoints: KeyPoint[] = [
		{
			title: "What personal information do we process?",
			content:
				"When you visit, use, or navigate our Services, we may process personal information depending on how you interact with us and the Services, the choices you make, and the products and features you use.",
			linkTo: 1
		},
		{
			title: "Do we process any sensitive personal information?",
			content: `Some of the information may be considered "special" or "sensitive" in certain jurisdictions, for example your racial or ethnic origins, sexual orientation, and religious beliefs. We do not process sensitive personal information.`
		},
		{
			title: "Do we collect any information from third parties?",
			content: "We do not collect any information from third parties."
		},
		{
			title: "How do we process your information?",
			content:
				"We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law. We may also process your information for other purposes with your consent. We process your information only when we have a valid legal reason to do so.",
			linkTo: 2
		},
		{
			title: "In what situations and with which parties do we share personal information?",
			content:
				"We may share information in specific situations and with specific third parties.",
			linkTo: 3
		},
		{
			title: "How do we keep your information safe?",
			content:
				"We have adequate organizational and technical processes and procedures in place to protect your personal information. However, no electronic transmission over the internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security and improperly collect, access, steal, or modify your information.",
			linkTo: 6
		},
		{
			title: "What are your rights?",
			content:
				"Depending on where you are located geographically, the applicable privacy law may mean you have certain rights regarding your personal information.",
			linkTo: 8
		},
		{
			title: "How do you exercise your rights?",
			content: `The easiest way to exercise your rights is by visiting ${siteUrl}${contactUrl}, or by contacting us. We will consider and act upon any request in accordance with applicable data protection laws.`
		}
	];
</script>

<div class="mx-auto flex w-[380px] flex-col gap-4 p-10 lg:w-[1140px]">
	<h1 class="text-3xl font-bold">Privacy Policy</h1>
	<p class="text-sm">Last updated {lastUpdated}</p>
	<p class="text-sm">
		This Privacy Notice for {entityName} ("we," "us," or "our"), describes how and why we might access,
		collect, store, use, and/or share ("process") your personal information when you use our services
		("Services"), including when you:
	</p>
	<li>
		Visit our website at {siteUrl}/, or any website of ours that links to this Privacy Notice
	</li>
	<li>Engage with us in other related ways, including any sales, marketing, or events</li>
	<p>
		Questions or concerns? Reading this Privacy Notice will help you understand your privacy
		rights and choices. We are responsible for making decisions about how your personal
		information is processed. If you do not agree with our policies and practices, please do not
		use our Services. If you still have any questions or concerns, please contact us at
		{email}.
	</p>

	<h2 class="text-2xl font-bold">Summary of Key Points</h2>
	<p class="italic">
		This summary provides key points from our Privacy Notice, but you can find out more details
		about any of these topics by clicking the link following each key point or by using our
		table of contents below to find the section you are looking for.
	</p>

	<div class="space-y-4">
		{#each keyPoints as point (point.title)}
			<div>
				<p class="font-bold">{point.title}</p>
				<p>
					{point.content}
					{#if point.linkTo}
						Learn more about <a
							href={`#${point.linkTo}`}
							class="text-primary hover:underline"
							>{sections[point.linkTo - 1].title}</a
						>.
					{/if}
				</p>
			</div>
		{/each}
	</div>

	<h2 class="text-2xl font-bold">Table of Contents</h2>
	<ul class="list-disc pl-4">
		{#each sections as section, index (section.title)}
			<ul class="text-blue-700">
				<a href={`#${(index + 1).toString()}`}>{section.title}</a>
			</ul>
		{/each}
	</ul>

	{#each sections as section, index (section.title)}
		<section id={(index + 1).toString()} class="flex flex-col gap-4">
			<h3 class="text-xl font-bold">{section.title}</h3>
			{#if section.inShort}
				<p><span class="mr-1 font-bold">In Short:</span>{section.inShort}</p>
			{/if}
			<p>{section.data}</p>

			{#if section.subsections}
				{#each section.subsections as subsection (subsection.title)}
					<div class="ml-4">
						<h4 class="text-lg font-bold">{subsection.title}</h4>
						{#if subsection.inShort}
							<p><span class="mr-1 font-bold">In Short:</span>{subsection.inShort}</p>
						{/if}
						<p class="whitespace-pre-line">{subsection.content}</p>

						{#if subsection.list}
							{#if subsection.list[0] && isPersonalInfoCategory(subsection.list[0])}
								<div class="overflow-x-auto">
									<table class="w-full border-collapse">
										<thead>
											<tr>
												<th class="border p-2 text-left">Category</th>
												<th class="border p-2 text-left">Examples</th>
												<th class="border p-2 text-center">Collected</th>
											</tr>
										</thead>
										<tbody>
											{#each subsection.list as item, i (typeof item === "string" ? i : item)}
												{#if isPersonalInfoCategory(item)}
													<tr>
														<td class="border p-2 font-medium"
															>{item.title}</td
														>
														<td class="border p-2">{item.content}</td>
														<td class="border p-2 text-center"
															>{item.collected}</td
														>
													</tr>
												{/if}
											{/each}
										</tbody>
									</table>
								</div>
							{:else if subsection.list.every((item) => typeof item === "string")}
								<ul class="ml-6 list-disc">
									{#each subsection.list as item, i (i)}
										<li>{item as string}</li>
									{/each}
								</ul>
							{:else}
								<div class="space-y-4">
									{#each subsection.list as item, i (typeof item === "string" ? i : item)}
										{#if typeof item === "object" && "title" in item}
											<div>
												<h5 class="font-bold italic">{item.title}</h5>
												<p>{item.content}</p>
											</div>
										{/if}
									{/each}
								</div>
							{/if}
						{/if}

						{#if subsection.additional}
							{#each subsection.additional as add (add)}
								<div class="mt-4">
									<h5 class="font-bold">{add.title}</h5>
									<p class="whitespace-pre-line">{add.content}</p>
								</div>
							{/each}
						{/if}

						{#if subsection.footer}
							<p class="mt-4">{subsection.footer}</p>
						{/if}
					</div>
				{/each}
			{/if}
		</section>
	{/each}
</div>
